import { useCallback, useEffect, useMemo } from 'react';
import * as Actions from '../firebase/actions';
import { ModuleStateEnvelope, ModuleStorageKey } from '../types';
import { useFirestoreState } from './useFirestore';

const MODULE_STATE_VERSION = 1;
const LOCAL_STATE_PREFIX = 'crypto-missions:local:';
const MODULE_STORAGE_KEY_PREFIX = 'crypto-missions:';

type StateUpdater<T> = T | ((current: T) => T);

interface UseModuleStateOptions {
  storageKey?: string;
  migrate?: (value: unknown, defaultData: any) => unknown;
  sanitize?: (value: TLike, defaultData: TLike) => TLike;
}

type TLike = any;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function cloneDefaultValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => cloneDefaultValue(item)) as T;
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, cloneDefaultValue(item)])
    ) as T;
  }

  return value;
}

function recoverValueShape<T>(value: unknown, defaultData: T): T {
  if (Array.isArray(defaultData)) {
    return Array.isArray(value) ? value as T : cloneDefaultValue(defaultData);
  }

  if (isPlainObject(defaultData)) {
    if (!isPlainObject(value)) {
      return cloneDefaultValue(defaultData);
    }

    const recoveredEntries = Object.entries(defaultData).map(([key, defaultValue]) => {
      const nextValue = key in value
        ? recoverValueShape(value[key], defaultValue)
        : cloneDefaultValue(defaultValue);
      return [key, nextValue];
    });

    const passthroughEntries = Object.entries(value).filter(([key]) => !(key in defaultData));
    return Object.fromEntries([...recoveredEntries, ...passthroughEntries]) as T;
  }

  if (typeof defaultData === 'number') {
    return (typeof value === 'number' && Number.isFinite(value) ? value : defaultData) as T;
  }

  if (typeof defaultData === 'string') {
    return (typeof value === 'string' ? value : defaultData) as T;
  }

  if (typeof defaultData === 'boolean') {
    return (typeof value === 'boolean' ? value : defaultData) as T;
  }

  if (defaultData === null) {
    return (value === null ? value : defaultData) as T;
  }

  if (defaultData === undefined) {
    return (value === undefined ? defaultData : value) as T;
  }

  return (value ?? defaultData) as T;
}

function isModuleStateEnvelope<T>(value: unknown): value is ModuleStateEnvelope<T> {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<ModuleStateEnvelope<T>>;
  return (
    typeof candidate.version === 'number' &&
    typeof candidate.updatedAt === 'number' &&
    'data' in candidate
  );
}

export function buildModuleStateEnvelope<T>(data: T): ModuleStateEnvelope<T> {
  return {
    version: MODULE_STATE_VERSION,
    updatedAt: Date.now(),
    data,
  };
}

function normalizeEnvelope<T>(
  value: unknown,
  defaultData: T,
  migrate?: (value: unknown, defaultData: T) => T | null,
  sanitize?: (value: T, defaultData: T) => T
): ModuleStateEnvelope<T> {
  if (!isModuleStateEnvelope<T>(value)) {
    const migratedData = migrate?.(value, defaultData) ?? null;
    if (migratedData !== null) {
      const recoveredData = recoverValueShape(migratedData, defaultData);
      return buildModuleStateEnvelope(sanitize ? sanitize(recoveredData, defaultData) : recoveredData);
    }
    return buildModuleStateEnvelope(cloneDefaultValue(defaultData));
  }

  const recoveredData = value.data === undefined
    ? cloneDefaultValue(defaultData)
    : recoverValueShape(value.data, defaultData);
  const sanitizedData = sanitize
    ? sanitize(recoveredData, defaultData)
    : recoveredData;

  return {
    version: Number.isFinite(value.version) ? value.version : MODULE_STATE_VERSION,
    updatedAt: Number.isFinite(value.updatedAt) ? value.updatedAt : Date.now(),
    data: sanitizedData,
  };
}

function readLocalEnvelope<T>(storageKey: string): ModuleStateEnvelope<T> | null {
  try {
    const rawValue = localStorage.getItem(`${LOCAL_STATE_PREFIX}${storageKey}`);
    return rawValue ? JSON.parse(rawValue) as ModuleStateEnvelope<T> : null;
  } catch (error) {
    console.error(`Failed to read local module state for ${storageKey}:`, error);
    return null;
  }
}

function writeLocalEnvelope<T>(storageKey: string, envelope: ModuleStateEnvelope<T>) {
  try {
    localStorage.setItem(`${LOCAL_STATE_PREFIX}${storageKey}`, JSON.stringify(envelope));
  } catch (error) {
    console.error(`Failed to cache local module state for ${storageKey}:`, error);
  }
}

export async function resetModuleStateStorage<T>(storageKey: string, defaultData: T) {
  const nextEnvelope = buildModuleStateEnvelope(cloneDefaultValue(defaultData));
  writeLocalEnvelope(storageKey, nextEnvelope);
  await Actions.handleModuleStateChange(storageKey, nextEnvelope);
}

export function isModuleStorageKey(storageKey: string): storageKey is ModuleStorageKey {
  return storageKey.startsWith(MODULE_STORAGE_KEY_PREFIX);
}

export function getModuleStorageKey(moduleIdOrKey: string): ModuleStorageKey {
  return (
    isModuleStorageKey(moduleIdOrKey)
      ? moduleIdOrKey
      : `${MODULE_STORAGE_KEY_PREFIX}modules:${moduleIdOrKey}`
  ) as ModuleStorageKey;
}

const useModuleState = <T>(
  moduleIdOrKey: string,
  defaultData: T,
  options?: UseModuleStateOptions
) => {
  const storageKey = options?.storageKey ?? getModuleStorageKey(moduleIdOrKey);
  const migrate = options?.migrate as ((value: unknown, defaultData: T) => T | null) | undefined;
  const sanitize = options?.sanitize as ((value: T, defaultData: T) => T) | undefined;
  const remoteState = useFirestoreState<unknown>(storageKey, null);
  const remoteEnvelope = remoteState.value;
  const localEnvelope = useMemo(() => readLocalEnvelope<T>(storageKey), [storageKey]);

  const resolvedEnvelope = useMemo(() => {
    if (remoteEnvelope) return normalizeEnvelope(remoteEnvelope, defaultData, migrate, sanitize);
    if (localEnvelope) return normalizeEnvelope(localEnvelope, defaultData, migrate, sanitize);
    return buildModuleStateEnvelope(cloneDefaultValue(defaultData));
  }, [defaultData, localEnvelope, migrate, remoteEnvelope, sanitize]);

  useEffect(() => {
    if (remoteEnvelope) {
      writeLocalEnvelope(storageKey, normalizeEnvelope(remoteEnvelope, defaultData, migrate, sanitize));
    }
  }, [defaultData, migrate, remoteEnvelope, sanitize, storageKey]);

  const saveState = useCallback(async (updater: StateUpdater<T>) => {
    const nextData = typeof updater === 'function'
      ? (updater as (current: T) => T)(resolvedEnvelope.data)
      : updater;
    const nextEnvelope = buildModuleStateEnvelope(nextData);
    writeLocalEnvelope(storageKey, nextEnvelope);
    await Actions.handleModuleStateChange(storageKey, nextEnvelope);
  }, [resolvedEnvelope.data, storageKey]);

  const clearState = useCallback(async () => {
    await resetModuleStateStorage(storageKey, defaultData);
  }, [defaultData, storageKey]);

  return {
    storageKey,
    envelope: resolvedEnvelope,
    state: resolvedEnvelope.data,
    isHydrated: remoteState.isHydrated,
    hasRemoteValue: remoteState.hasRemoteValue,
    error: remoteState.error,
    saveState,
    clearState,
  };
};

export default useModuleState;
